import {
  agendaRruleSchema,
  agendaSlotTypeSchema,
  BUSINESS_TIMEZONE,
  daysOfWeek,
  GetWorkScheduleSlotListApiPayload,
  PRESENTIEL_SLOT_TYPE,
  periodsOfTheDay,
  UnauthorizedError,
} from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import http from '@/utils/http.service';

export const workScheduleSlotSequenceSchema = z.object({
  id: z.string().uuid(),
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
  rrule: agendaRruleSchema,
  exdate: z.array(z.string()),
  sourceId: z.string().nullable().default(null),
  dayOfWeek: z.enum(daysOfWeek),
  periodOfTheDay: z.enum(periodsOfTheDay),
  occurenceIndex: z.number().optional(),
});
export type WorkScheduleSlotSequence = z.infer<
  typeof workScheduleSlotSequenceSchema
>;

export const getWorkScheduleSlotItemSchema = z.object({
  id: z.string().uuid(),
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
  periodOfTheDay: z.enum(periodsOfTheDay),
  doctorAttending: z.object({
    id: z.string().uuid(),
    firstname: z.string(),
    lastname: z.string(),
    rhythm: z.string().optional(),
    onSiteSlotsWithoutAssistant: z.number().nullable().optional(),
    remoteSlotsWithoutAssistant: z.number().nullable().optional(),
    onSiteSlotsWithAssistant: z.number().nullable().optional(),
    remoteSlotsWithAssistant: z.number().nullable().optional(),
    rhythmNote: z.string().nullable().optional(),
  }),
  rrule: agendaRruleSchema,
  exdate: z.array(z.string()),
  sourceId: z.string().nullable().default(null),
  sequence: workScheduleSlotSequenceSchema,
  occurenceIndex: z.number().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  slotType: agendaSlotTypeSchema.optional().default(PRESENTIEL_SLOT_TYPE),
  withAssistante: z.coerce.boolean().optional(),
  forceWithoutAssistant: z.coerce.boolean().optional().default(false),
  medicalAssistantWorkDuration: z.number().optional(),
  medicalAssistantWorkPeriods: z
    .array(
      z.object({
        startTime: z.coerce.date(),
        endTime: z.coerce.date(),
        date: z.coerce.date(),
      }),
    )
    .optional(),
  // Computed assistant periods (day-boundary extended, pauses removed, Formation
  // excluded), resolved server-side. Preferred over the legacy Skello periods.
  assistantPeriods: z
    .array(
      z.object({
        assistantId: z.string(),
        userId: z.string(),
        firstname: z.string(),
        lastname: z.string(),
        startTime: z.string(),
        endTime: z.string(),
        durationMinutes: z.number(),
      }),
    )
    .optional(),
  reconciliationWarningType: z.string().nullable().optional(),
  tag: z.string().optional(),
});
export type GetWorkScheduleSlotItemResponse = z.infer<
  typeof getWorkScheduleSlotItemSchema
>;

export const getWorkScheduleSlotsListSchema = z.object({
  data: z.array(getWorkScheduleSlotItemSchema),
});

export type GetWorkScheduleSlotsListResponse = z.infer<
  typeof getWorkScheduleSlotsListSchema
>;
export type GetWorkScheduleSlotsPayload = Omit<
  GetWorkScheduleSlotListApiPayload,
  'timezone'
>;
/**
 * Query key for one office's work-schedule slots over a date range.
 *
 * @param params - The office id and the date range to load.
 * @returns The React Query key.
 */
/** Root of every office work-schedule-slots key, for range-wide invalidation. */
export const OFFICE_WORK_SCHEDULE_SLOTS_QUERY_ROOT =
  'office-work-schedule-slots';

export const getOfficeWorkScheduleSlotsQueryKey = (
  params: GetWorkScheduleSlotsPayload,
) => [
  OFFICE_WORK_SCHEDULE_SLOTS_QUERY_ROOT,
  params.officeId,
  params.from?.toISOString(),
  params.to?.toISOString(),
];

export const getOfficeWorkScheduleSlots =
  (params: GetWorkScheduleSlotsPayload) => async () => {
    return http.workScheduleSlots
      .getList({
        ...params,
        timezone: BUSINESS_TIMEZONE,
      })
      .then<GetWorkScheduleSlotsListResponse>(({ response, transform }) => {
        const result = transform.handle({
          response,
          attributes: {
            startAt: true,
            endAt: true,
            rrule: true,
            exdate: true,
            sourceId: true,
            occurenceIndex: true,
            slotType: true,
            withAssistante: true,
            forceWithoutAssistant: true,
            dayOfWeek: true,
            periodOfTheDay: true,
            createdAt: true,
            updatedAt: true,
            medicalAssistantWorkDuration: true,
            medicalAssistantWorkPeriods: true,
            assistantPeriods: true,
            reconciliationWarningType: true,
            tag: true,
          },
          relations: {
            doctorAttending: true,
            sequence: true,
          },
          schema: getWorkScheduleSlotsListSchema,
        });

        return {
          data: result.data as GetWorkScheduleSlotsListResponse['data'],
        };
      });
  };

export const useGetOfficeWorkScheduleSlotsQuery = (
  params: GetWorkScheduleSlotsPayload,
  enabled: boolean,
): UseQueryResult<GetWorkScheduleSlotsListResponse, UnauthorizedError> => {
  return useQuery({
    queryFn: getOfficeWorkScheduleSlots(params),
    queryKey: getOfficeWorkScheduleSlotsQueryKey(params),
    retry: false,
    enabled,
  });
};
